import React, { useState, useEffect, ChangeEvent, MouseEvent } from 'react';
import {
  Box,
  Typography,
  Container,
  Grid,
  TextField,
  Button,
  Paper,
  CircularProgress,
  IconButton,
  Alert,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Switch,
  FormControlLabel,
  Card,
  CardContent,
  CardMedia,
  Divider,
  Chip,
  InputAdornment,
  MenuItem,
  SelectChangeEvent,
  FormControl,
  InputLabel,
  Select,
  List,
  ListItem,
  ListItemButton,
  Avatar,
  Menu,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
  FormHelperText
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Category as CategoryIcon,
  Refresh as RefreshIcon,
  Image as ImageIcon,
  Link as LinkIcon,
  CloudUpload as CloudUploadIcon,
  ArrowUpward as ArrowUpwardIcon,
  ArrowDownward as ArrowDownwardIcon,
  SwapVert as SwapVertIcon,
  MoreVert as MoreVertIcon,
  ArrowForward as ArrowForwardIcon,
  SubdirectoryArrowRight as SubdirectoryArrowRightIcon,
  FolderSpecial as FolderSpecialIcon,
  Folder as FolderIcon,
  Upload as UploadIcon
} from '@mui/icons-material';
import {
  addCategory,
  getMainCategories,
  getAllCategories,
  getSubcategories,
  updateCategory,
  deleteCategory,
  getCategoryById,
  moveCategoryPosition,
  convertToSubcategory,
  convertToMainCategory,
  deleteAllCategories
} from '../services/categoryService';
import { fileToBase64 } from '../services/uploadService';
import { Category, SubCategory } from '../types/category';
import { debug, logError } from '../utils/debug';
import { uploadImage as uploadToCloudinary } from '../services/cloudinaryService';

// نموذج الفئة المستخدم في النموذج
interface CategoryFormData {
  id?: string;
  name: string;
  description: string;
  parentId?: string;
  imageUrl?: string;
  isActive: boolean;
  order?: number;
}

const CategoryPage: React.FC = () => {
  // حالة الفئات
  const [mainCategories, setMainCategories] = useState<Category[]>([]);
  const [allCategories, setAllCategories] = useState<Category[]>([]);
  const [subcategories, setSubcategories] = useState<SubCategory[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<Category | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // حالة النموذج
  const [openDialog, setOpenDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [isSubcategory, setIsSubcategory] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [currentCategory, setCurrentCategory] = useState<CategoryFormData>({
    name: '',
    description: '',
    parentId: undefined,
    imageUrl: '',
    isActive: true
  });
  const [selectedImage, setSelectedImage] = useState<File | null>(null);
  const [previewImage, setPreviewImage] = useState<string | null>(null);

  // حالة الإشعارات
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error' | 'info' | 'warning',
  });
  
  // حالة قائمة الإجراءات الإضافية
  const [actionMenuAnchorEl, setActionMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedActionCategory, setSelectedActionCategory] = useState<Category | null>(null);
  
  // حالة حوار تحويل الفئة
  const [convertCategoryDialog, setConvertCategoryDialog] = useState(false);
  const [convertTargetParent, setConvertTargetParent] = useState<string>('');

  // هنا نضيف حالة لحوار تأكيد حذف كافة الفئات
  const [openDeleteAllDialog, setOpenDeleteAllDialog] = useState(false);
  const [isDeletingAll, setIsDeletingAll] = useState(false);

  // تحميل بيانات الفئات
  const loadCategories = async () => {
    try {
      setLoading(true);
      
      // حفظ المعرف الحالي للفئة المحددة إن وجدت
      const currentSelectedId = selectedCategory?.id;
      
      // تحميل الفئات الرئيسية والفئات الأخرى
      const [fetchedMainCats, fetchedAllCats] = await Promise.all([
        getMainCategories(),
        getAllCategories()
      ]);
      
      // تحويل البيانات المستلمة إلى النوع المناسب
      const transformedMainCats: Category[] = fetchedMainCats.map(cat => ({
        id: cat.id,
        name: cat.name || '',
        description: cat.description || '',
        parentId: cat.parentId,
        order: cat.order,
        isActive: cat.isActive === undefined ? true : cat.isActive,
        imageUrl: cat.imageUrl,
        createdAt: typeof cat.createdAt === 'string' ? new Date(cat.createdAt) : cat.createdAt,
        updatedAt: typeof cat.updatedAt === 'string' ? new Date(cat.updatedAt) : cat.updatedAt
      }));
      
      const transformedAllCats: Category[] = fetchedAllCats.map(cat => ({
        id: cat.id,
        name: cat.name || '',
        description: cat.description || '',
        parentId: cat.parentId,
        order: cat.order,
        isActive: cat.isActive === undefined ? true : cat.isActive,
        imageUrl: cat.imageUrl,
        createdAt: typeof cat.createdAt === 'string' ? new Date(cat.createdAt) : cat.createdAt,
        updatedAt: typeof cat.updatedAt === 'string' ? new Date(cat.updatedAt) : cat.updatedAt
      }));
      
      // تحديث حالة الفئات
      setMainCategories(transformedMainCats);
      setAllCategories(transformedAllCats);
      
      // إذا كانت هناك فئة محددة، نحاول الحفاظ عليها
      if (currentSelectedId) {
        // البحث عن الفئة في الفئات المحدثة
        const updatedCategory = transformedAllCats.find(c => c.id === currentSelectedId);
        
        if (updatedCategory) {
          // تحديث الفئة المحددة بالنسخة المحدثة
          setSelectedCategory(updatedCategory);
        }
      }
      
      setLoading(false);
      
      console.log(`تم تحميل ${transformedMainCats.length} فئة رئيسية و ${transformedAllCats.length} فئة إجمالية`);
    } catch (err) {
      console.error('Error loading categories:', err);
      setError('حدث خطأ أثناء تحميل الفئات');
      setLoading(false);
      
      // محاولة استخدام البيانات المخزنة محليًا إذا كانت متوفرة
      try {
        if (typeof localStorage !== 'undefined') {
          const mainCategoriesCache = localStorage.getItem('main_categories');
          if (mainCategoriesCache) {
            const cachedMainCats = JSON.parse(mainCategoriesCache);
            
            // تحويل البيانات المخزنة محليًا إلى النوع المناسب
            const mainCats: Category[] = cachedMainCats.map((cat: any) => ({
              id: cat.id,
              name: cat.name || '',
              description: cat.description || '',
              parentId: cat.parentId,
              order: cat.order,
              isActive: cat.isActive === undefined ? true : cat.isActive,
              imageUrl: cat.imageUrl,
              createdAt: typeof cat.createdAt === 'string' ? new Date(cat.createdAt) : cat.createdAt,
              updatedAt: typeof cat.updatedAt === 'string' ? new Date(cat.updatedAt) : cat.updatedAt
            }));
            
            setMainCategories(mainCats);
            console.log(`تم استخدام ${mainCats.length} فئة رئيسية من التخزين المحلي`);
          }
          
          const categoriesCache = localStorage.getItem('categories');
          if (categoriesCache) {
            const cachedAllCats = JSON.parse(categoriesCache);
            
            // تحويل البيانات المخزنة محليًا إلى النوع المناسب
            const allCats: Category[] = cachedAllCats.map((cat: any) => ({
              id: cat.id,
              name: cat.name || '',
              description: cat.description || '',
              parentId: cat.parentId,
              order: cat.order,
              isActive: cat.isActive === undefined ? true : cat.isActive,
              imageUrl: cat.imageUrl,
              createdAt: typeof cat.createdAt === 'string' ? new Date(cat.createdAt) : cat.createdAt,
              updatedAt: typeof cat.updatedAt === 'string' ? new Date(cat.updatedAt) : cat.updatedAt
            }));
            
            setAllCategories(allCats);
            console.log(`تم استخدام ${allCats.length} فئة إجمالية من التخزين المحلي`);
            
            // محاولة استعادة الفئة المحددة من البيانات المخزنة
            if (selectedCategory) {
              const cachedSelected = allCats.find((c: Category) => c.id === selectedCategory.id);
              if (cachedSelected) {
                setSelectedCategory(cachedSelected);
              }
            }
          }
        }
      } catch (cacheError) {
        console.error('Error loading categories from cache:', cacheError);
      }
    }
  };

  // تحميل الفئات الفرعية عند تغيير الفئة المحددة
  useEffect(() => {
    let isMounted = true;
    
    const fetchSubcategories = async () => {
      if (!selectedCategory || !selectedCategory.id) {
        // إذا لم تكن هناك فئة محددة أو كان المعرف فارغًا، قم بتفريغ الفئات الفرعية
        if (isMounted) {
          setSubcategories([]);
        }
        return;
      }
      
      if (isMounted) {
        // إظهار مؤشر التحميل أولاً
        setLoading(true);
        
        try {
          console.log(`تحميل الفئات الفرعية للفئة: ${selectedCategory.name} (${selectedCategory.id})`);
          
          // محاولة استخدام التخزين المؤقت المحلي أولًا
          let cachedSubcats = null;
          
          try {
            if (typeof localStorage !== 'undefined') {
              const subcategoriesKey = `subcategories_${selectedCategory.id}`;
              const cachedData = localStorage.getItem(subcategoriesKey);
              const timestampStr = localStorage.getItem(`${subcategoriesKey}_timestamp`);
              
              if (cachedData && timestampStr) {
                const timestamp = parseInt(timestampStr, 10);
                const now = Date.now();
                // استخدام التخزين المؤقت إذا كان عمره أقل من 5 دقائق
                if (now - timestamp < 5 * 60 * 1000) {
                  cachedSubcats = JSON.parse(cachedData);
                  console.log(`استخدام ${cachedSubcats.length} فئة فرعية من التخزين المؤقت`);
                }
              }
            }
          } catch (cacheError) {
            console.warn('خطأ في قراءة الفئات الفرعية من التخزين المؤقت:', cacheError);
          }
          
          // إذا كان هناك فئات فرعية في التخزين المؤقت، استخدمها مؤقتًا ثم قم بتحديثها
          if (cachedSubcats && cachedSubcats.length > 0 && isMounted) {
            setSubcategories(cachedSubcats);
          }
          
          // تحميل الفئات الفرعية من الخدمة
          const fetchedSubs = await getSubcategories(selectedCategory.id);
          
          if (isMounted) {
            // تحويل البيانات المستلمة إلى النوع المناسب إذا لزم الأمر
            const transformedSubs: SubCategory[] = fetchedSubs.map(sub => ({
              id: sub.id,
              name: sub.name || '',
              description: sub.description || '',
              categoryId: sub.categoryId || sub.parentId || selectedCategory.id,
              parentId: sub.parentId || selectedCategory.id,
              order: sub.order || 0,
              isActive: sub.isActive === undefined ? true : sub.isActive,
              imageUrl: sub.imageUrl || '',
              createdAt: typeof sub.createdAt === 'string' ? new Date(sub.createdAt) : (sub.createdAt || new Date()),
              updatedAt: typeof sub.updatedAt === 'string' ? new Date(sub.updatedAt) : (sub.updatedAt || new Date())
            }));
            
            console.log(`تم تحميل ${transformedSubs.length} فئة فرعية`);
            setSubcategories(transformedSubs);
            setLoading(false);
          }
        } catch (err) {
          console.error('Error loading subcategories:', err);
          if (isMounted) {
            setLoading(false);
            // إظهار رسالة الخطأ فقط إذا لم يكن هناك بيانات موجودة بالفعل
            if (subcategories.length === 0) {
              setSnackbar({
                open: true,
                message: 'حدث خطأ أثناء تحميل الفئات الفرعية',
                severity: 'error',
              });
            }
          }
        }
      }
    };
    
    fetchSubcategories();
    
    // تنظيف عند إلغاء تحميل المكون
    return () => {
      isMounted = false;
    };
  }, [selectedCategory?.id]); // استخدام selectedCategory.id كتبعية فقط

  // تحميل البيانات عند التحميل الأولي
  useEffect(() => {
    loadCategories();
  }, []);

  // اختيار فئة
  const handleSelectCategory = (category: Category) => {
    setSelectedCategory(category);
  };

  // فتح نموذج إضافة فئة جديدة
  const handleAddCategory = (isSubcat: boolean = false) => {
    setCurrentCategory({
      name: '',
      description: '',
      parentId: isSubcat ? (selectedCategory ? selectedCategory.id : undefined) : undefined,
      imageUrl: '',
      isActive: true
    });
    setSelectedImage(null);
    setPreviewImage(null);
    setIsEditMode(false);
    setIsSubcategory(isSubcat);
    setOpenDialog(true);
  };

  // فتح نموذج تعديل فئة
  const handleEditCategory = async (categoryId: string) => {
    try {
      const category = await getCategoryById(categoryId);
      if (category) {
        // تحويل الفئة إلى نموذج البيانات
        const formData: CategoryFormData = {
          id: category.id,
          name: category.name || '',
          description: category.description || '',
          parentId: category.parentId,
          imageUrl: category.imageUrl,
          isActive: category.isActive === undefined ? true : category.isActive,
          order: category.order
        };
        
        setCurrentCategory(formData);
        setSelectedImage(null);
        setPreviewImage(category.imageUrl || null);
        setIsEditMode(true);
        setIsSubcategory(!!category.parentId);
        setOpenDialog(true);
      }
    } catch (err) {
      console.error('Error getting category details:', err);
      setSnackbar({
        open: true,
        message: 'حدث خطأ أثناء تحميل بيانات الفئة',
        severity: 'error',
      });
    }
  };

  // فتح حوار تأكيد الحذف
  const handleDeleteConfirm = (category: Category) => {
    // تحويل الفئة إلى نموذج البيانات
    const formData: CategoryFormData = {
      id: category.id,
      name: category.name || '',
      description: category.description || '',
      parentId: category.parentId,
      imageUrl: category.imageUrl,
      isActive: category.isActive === undefined ? true : category.isActive,
      order: category.order
    };
    
    setCurrentCategory(formData);
    setOpenDeleteDialog(true);
  };

  // تغيير قيمة في النموذج
  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setCurrentCategory(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  // تغيير الفئة الرئيسية في حالة الفئة الفرعية
  const handleParentChange = (e: SelectChangeEvent<string>) => {
    setCurrentCategory(prev => ({
      ...prev,
      parentId: e.target.value
    }));
  };

  // تحميل صورة
  const handleImageUpload = async (e: ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    
    const file = files[0];
    
    // التحقق من نوع الملف
    if (!file.type.startsWith('image/')) {
      setSnackbar({
        open: true,
        message: 'يرجى اختيار ملف صورة صالح',
        severity: 'error',
      });
      return;
    }
    
    // التحقق من حجم الملف (5 ميجابايت كحد أقصى)
    const maxSize = 5 * 1024 * 1024; // 5MB
    if (file.size > maxSize) {
      setSnackbar({
        open: true,
        message: 'حجم الصورة كبير جدًا. يرجى اختيار صورة أقل من 5 ميغابايت',
        severity: 'error',
      });
      return;
    }
    
    setSelectedImage(file);
    
    try {
      // إنشاء معاينة للصورة
      const reader = new FileReader();
      reader.onload = (event) => {
        if (event.target && typeof event.target.result === 'string') {
          setPreviewImage(event.target.result);
        }
      };
      reader.readAsDataURL(file);
    } catch (error) {
      console.error('Error creating image preview:', error);
      setPreviewImage(null);
    }
  };

  // اختبار خدمة التخزين للصور
  const handleTestStorage = async () => {
    try {
      setIsSaving(true);
      
      setSnackbar({
        open: true,
        message: 'جاري اختبار خدمة تخزين الصور...',
        severity: 'info',
      });
      
      // اختبار خدمة Cloudinary
      try {
        const testResult = await fetch('https://api.cloudinary.com/v1_1/' + 
          uploadToCloudinary.toString().match(/cloud_name=([^&\"]+)/)?.[1] + '/ping')
          .then(response => response.json())
          .catch(() => ({ status: 'error' }));
          
        if (testResult.status === 'ok') {
          setSnackbar({
            open: true,
            message: 'خدمة Cloudinary تعمل بشكل صحيح',
            severity: 'success',
          });
          return;
        }
      } catch (cloudinaryError) {
        console.error('خطأ في اختبار Cloudinary:', cloudinaryError);
      }
      
      // التحقق من وظيفة التحويل إلى base64 كبديل
      try {
        // إنشاء صورة فارغة للاختبار
        const canvas = document.createElement('canvas');
        canvas.width = 100;
        canvas.height = 100;
        const testBlob = await new Promise<Blob>((resolve) => {
          canvas.toBlob(blob => resolve(blob!), 'image/png');
        });
        
        if (testBlob) {
          const testFile = new File([testBlob], 'test.png', { type: 'image/png' });
          const base64Result = await fileToBase64(testFile);
          
          if (base64Result) {
            setSnackbar({
              open: true,
              message: 'خدمة التخزين المحلي تعمل كبديل',
              severity: 'success',
            });
            return;
          }
        }
      } catch (base64Error) {
        console.error('خطأ في اختبار التحويل إلى base64:', base64Error);
      }
      
      // إذا وصلنا إلى هنا، فهذا يعني أن جميع الاختبارات فشلت
      setSnackbar({
        open: true,
        message: 'خدمات تخزين الصور غير متاحة. يمكنك متابعة العمل بدون صور.',
        severity: 'warning',
      });
    } catch (error) {
      console.error('خطأ في اختبار خدمة التخزين:', error);
      setSnackbar({
        open: true,
        message: 'فشل اختبار خدمة التخزين',
        severity: 'error',
      });
    } finally {
      setIsSaving(false);
    }
  };

  // حفظ الفئة (إضافة أو تعديل)
  const handleSaveCategory = async () => {
    try {
      if (!currentCategory.name) {
        setSnackbar({
          open: true,
          message: 'يرجى إدخال اسم الفئة',
          severity: 'error',
        });
        return;
      }

      setIsSaving(true); // بدء عملية الحفظ

      let imageUrl = currentCategory.imageUrl || '';

      // تحميل الصورة إذا تم اختيارها
      if (selectedImage) {
        try {
          // استخدام Cloudinary فقط لرفع الصور وتجاهل Firebase Storage
          try {
            imageUrl = await uploadToCloudinary(selectedImage, 'categories');
            console.log('تم تحميل الصورة إلى Cloudinary بنجاح', imageUrl);
          } catch (cloudinaryError) {
            console.error('فشل التحميل إلى Cloudinary', cloudinaryError);
            
            // استخدام base64 كحل بديل إذا فشل الرفع
            try {
              const base64 = await fileToBase64(selectedImage);
              if (typeof base64 === 'string') {
                imageUrl = base64;
                console.log('تم تحويل الصورة إلى base64 بنجاح');
              }
            } catch (base64Error) {
              console.error('فشل تحويل الصورة إلى base64', base64Error);
              throw cloudinaryError; // رمي الخطأ الأصلي
            }
          }
        } catch (uploadError) {
          console.error('فشل تحميل الصورة', uploadError);
          
          setSnackbar({
            open: true,
            message: `خطأ في تحميل الصورة. سيتم المتابعة بدون صورة.`,
            severity: 'warning',
          });
          
          // المتابعة بدون صورة
          imageUrl = '';
        }
      }

      // الاستمرار في حفظ الفئة بعد تحميل الصورة بنجاح
      if (isEditMode && currentCategory.id) {
        // تعديل فئة موجودة
        const categoryData: Partial<Category> = {
          name: currentCategory.name,
          description: currentCategory.description,
          parentId: currentCategory.parentId,
          isActive: currentCategory.isActive,
          imageUrl,
          updatedAt: new Date()
        };
        
        console.log('تحديث الفئة الموجودة:', currentCategory.id, categoryData);
        await updateCategory(currentCategory.id, categoryData);
        setSnackbar({
          open: true,
          message: 'تم تحديث الفئة بنجاح',
          severity: 'success',
        });

        setOpenDialog(false);
        
        // تأخير قصير قبل إعادة تحميل البيانات
        setTimeout(async () => {
          try {
            await loadCategories();
            
            // إعادة تحميل الفئات الفرعية إذا كانت الفئة الحالية محددة
            if (selectedCategory && selectedCategory.id === currentCategory.id) {
              try {
                // إعادة تعيين التخزين المؤقت
                const cacheKey = `last_loaded_subcategories_${selectedCategory.id}`;
                sessionStorage.removeItem(cacheKey);
                
                // تحميل الفئات الفرعية الجديدة
                const fetchedSubs = await getSubcategories(selectedCategory.id);
                
                // تحويل البيانات
                const convertedSubs: SubCategory[] = fetchedSubs.map(sub => ({
                  id: sub.id,
                  name: sub.name || '',
                  description: sub.description || '',
                  categoryId: sub.categoryId || sub.parentId || '',
                  parentId: sub.parentId,
                  order: sub.order,
                  isActive: sub.isActive === undefined ? true : sub.isActive,
                  imageUrl: sub.imageUrl,
                  createdAt: typeof sub.createdAt === 'string' ? new Date(sub.createdAt) : sub.createdAt,
                  updatedAt: typeof sub.updatedAt === 'string' ? new Date(sub.updatedAt) : sub.updatedAt
                }));
                
                setSubcategories(convertedSubs);
                
                // تحديث وقت آخر تحميل
                sessionStorage.setItem(cacheKey, Date.now().toString());
              } catch (subcatError) {
                console.error('Error loading subcategories after save:', subcatError);
              }
            }
          } catch (reloadError) {
            console.error('Error reloading categories after save:', reloadError);
          } finally {
            setIsSaving(false);
          }
        }, 500);
      } else {
        // إضافة فئة جديدة
        const categoryData: Omit<Category, 'id' | 'createdAt' | 'updatedAt'> = {
          name: currentCategory.name,
          description: currentCategory.description || '',
          parentId: currentCategory.parentId,
          isActive: currentCategory.isActive,
          imageUrl,
          order: currentCategory.order || 0
        };
        
        console.log('إضافة فئة جديدة:', categoryData);
        const newCategoryId = await addCategory(categoryData);
        console.log('تم إضافة فئة جديدة بنجاح، المعرف:', newCategoryId);
        
        setSnackbar({
          open: true,
          message: 'تم إضافة الفئة بنجاح',
          severity: 'success',
        });
        
        setOpenDialog(false);
        
        // تأخير قصير قبل إعادة تحميل البيانات
        setTimeout(async () => {
          try {
            await loadCategories();
            
            // إذا كانت فئة جديدة وليست فئة فرعية، قم بتحديدها
            if (!isSubcategory && newCategoryId) {
              const newCategory = await getCategoryById(newCategoryId);
              if (newCategory) {
                setSelectedCategory(newCategory as Category);
              }
            }
            
            // إعادة تحميل الفئات الفرعية إذا كانت فئة فرعية
            if (isSubcategory && currentCategory.parentId && selectedCategory) {
              if (selectedCategory.id === currentCategory.parentId) {
                try {
                  // إعادة تعيين التخزين المؤقت
                  const cacheKey = `last_loaded_subcategories_${selectedCategory.id}`;
                  sessionStorage.removeItem(cacheKey);
                  
                  // تحميل الفئات الفرعية الجديدة
                  const fetchedSubs = await getSubcategories(selectedCategory.id);
                  
                  // تحويل البيانات
                  const convertedSubs: SubCategory[] = fetchedSubs.map(sub => ({
                    id: sub.id,
                    name: sub.name || '',
                    description: sub.description || '',
                    categoryId: sub.categoryId || sub.parentId || '',
                    parentId: sub.parentId,
                    order: sub.order,
                    isActive: sub.isActive === undefined ? true : sub.isActive,
                    imageUrl: sub.imageUrl,
                    createdAt: typeof sub.createdAt === 'string' ? new Date(sub.createdAt) : sub.createdAt,
                    updatedAt: typeof sub.updatedAt === 'string' ? new Date(sub.updatedAt) : sub.updatedAt
                  }));
                  
                  setSubcategories(convertedSubs);
                  
                  // تحديث وقت آخر تحميل
                  sessionStorage.setItem(cacheKey, Date.now().toString());
                } catch (subcatError) {
                  console.error('Error loading subcategories after save:', subcatError);
                }
              }
            }
          } catch (reloadError) {
            console.error('Error reloading categories after save:', reloadError);
          } finally {
            setIsSaving(false);
          }
        }, 500);
      }
    } catch (err) {
      console.error('Error saving category:', err);
      setSnackbar({
        open: true,
        message: `حدث خطأ أثناء حفظ الفئة: ${err instanceof Error ? err.message : 'خطأ غير معروف'}`,
        severity: 'error',
      });
      setIsSaving(false);
    }
  };

  // حذف الفئة
  const handleDeleteCategory = async () => {
    if (!currentCategory.id) {
      setSnackbar({
        open: true,
        message: 'لا يمكن حذف الفئة, معرف الفئة غير متوفر',
        severity: 'error'
      });
      return;
    }
    
    setIsSaving(true);
    
    try {
      await deleteCategory(currentCategory.id);
      
      setSnackbar({
        open: true,
        message: 'تم حذف الفئة بنجاح',
        severity: 'success'
      });
      
      // إعادة تحميل الفئات
      await loadCategories();
      
      if (selectedCategory && selectedCategory.id === currentCategory.id) {
        setSelectedCategory(null);
      }
      
      setOpenDeleteDialog(false);
    } catch (error) {
      console.error('Error deleting category:', error);
      setSnackbar({
        open: true,
        message: 'حدث خطأ أثناء حذف الفئة',
        severity: 'error'
      });
    } finally {
      setIsSaving(false);
    }
  };
  
  // فتح حوار تحويل الفئة
  const handleOpenConvertDialog = () => {
    setConvertTargetParent('');
    setConvertCategoryDialog(true);
    handleCloseActionMenu();
  };
  
  // تحويل فئة رئيسية إلى فئة فرعية
  const handleConvertToSubcategory = async () => {
    if (!selectedActionCategory || !convertTargetParent) return;
    
    try {
      setIsSaving(true);
      await convertToSubcategory(selectedActionCategory.id, convertTargetParent);
      
      setSnackbar({
        open: true,
        message: 'تم تحويل الفئة إلى فئة فرعية بنجاح',
        severity: 'success',
      });
      
      // إغلاق الحوار أولاً
      setConvertCategoryDialog(false);
      
      // تأخير قصير لتجنب التحديثات المتزامنة
      setTimeout(async () => {
        // حفظ المعرف المطلوب قبل إعادة تحميل البيانات
        const targetParentId = convertTargetParent;
        await loadCategories();
        
        // الحصول على الفئة الأب وتعيينها بشكل آمن
        const parentCategory = await getCategoryById(targetParentId);
        if (parentCategory) {
          setSelectedCategory(parentCategory as Category);
        }
        
        setIsSaving(false);
      }, 300);
    } catch (err: any) {
      setIsSaving(false);
      console.error('Error converting to subcategory:', err);
      setSnackbar({
        open: true,
        message: err.message || 'حدث خطأ أثناء تحويل الفئة',
        severity: 'error',
      });
    }
  };
  
  // تحويل فئة فرعية إلى فئة رئيسية
  const handleConvertToMainCategory = async () => {
    if (!selectedActionCategory) return;
    
    try {
      setIsSaving(true);
      const categoryToConvert = { ...selectedActionCategory }; // نسخة من الفئة قبل التحويل
      
      await convertToMainCategory(categoryToConvert.id);
      
      setSnackbar({
        open: true,
        message: 'تم تحويل الفئة الفرعية إلى فئة رئيسية بنجاح',
        severity: 'success',
      });
      
      // إغلاق القائمة أولاً
      handleCloseActionMenu();
      
      // تأخير قصير لتجنب التحديثات المتزامنة
      setTimeout(async () => {
        // إعادة تحميل البيانات
        await loadCategories();
        
        // إذا كانت الفئة المحددة حالياً هي الأب السابق، لا نقوم بتغييرها
        // وإلا نقوم بتحديد الفئة التي تم تحويلها
        if (!selectedCategory || (categoryToConvert.parentId && selectedCategory.id === categoryToConvert.parentId)) {
          // سنبقي على الفئة الأب محددة
        } else {
          // نحاول العثور على وتحديد الفئة المحولة
          const convertedCategory = await getCategoryById(categoryToConvert.id);
          if (convertedCategory) {
            setSelectedCategory(convertedCategory as Category);
          }
        }
        
        setIsSaving(false);
      }, 300);
    } catch (err: any) {
      setIsSaving(false);
      console.error('Error converting to main category:', err);
      setSnackbar({
        open: true,
        message: err.message || 'حدث خطأ أثناء تحويل الفئة',
        severity: 'error',
      });
    }
  };

  // دالة حذف كافة الفئات
  const handleDeleteAllCategories = async () => {
    try {
      setIsDeletingAll(true);
      await deleteAllCategories();
      
      setSnackbar({
        open: true,
        message: 'تم حذف جميع الفئات بنجاح',
        severity: 'success',
      });
      
      // إعادة تحميل البيانات
      await loadCategories();
      setSelectedCategory(null);
      setSubcategories([]);
      
    } catch (error) {
      console.error('Error deleting all categories:', error);
      setSnackbar({
        open: true,
        message: error instanceof Error 
          ? `فشل في حذف الفئات: ${error.message}` 
          : 'فشل في حذف الفئات',
        severity: 'error',
      });
    } finally {
      setIsDeletingAll(false);
      setOpenDeleteAllDialog(false);
    }
  };

  // إغلاق الإشعارات
  const handleCloseSnackbar = () => {
    setSnackbar({
      ...snackbar,
      open: false
    });
  };

  // تحديد ما إذا كانت الفئة محددة
  const isSelected = (category: Category): boolean => {
    return selectedCategory !== null && selectedCategory.id === category.id;
  };

  // فتح قائمة الإجراءات الإضافية
  const handleOpenActionMenu = (event: React.MouseEvent<HTMLElement>, category: Category) => {
    event.stopPropagation();
    setActionMenuAnchorEl(event.currentTarget);
    setSelectedActionCategory(category);
  };

  // إغلاق قائمة الإجراءات
  const handleCloseActionMenu = () => {
    setActionMenuAnchorEl(null);
  };

  // نقل الفئة للأعلى
  const handleMoveUp = async () => {
    if (!selectedActionCategory) {
      handleCloseActionMenu();
      return;
    }
    
    try {
      setIsSaving(true);
      await moveCategoryPosition(selectedActionCategory.id, 'up');
      
      setSnackbar({
        open: true,
        message: 'تم تحريك الفئة لأعلى بنجاح',
        severity: 'success',
      });
      
      // إغلاق القائمة المنبثقة
      handleCloseActionMenu();
      
      // إعادة تحميل البيانات بعد التحريك
      await loadCategories();
      
      // إذا كانت الفئة فرعية، نعيد تحميل الفئات الفرعية
      if (selectedActionCategory.parentId && selectedCategory) {
        const fetchedSubs = await getSubcategories(selectedCategory.id);
        const transformedSubs: SubCategory[] = fetchedSubs.map(sub => ({
          id: sub.id,
          name: sub.name || '',
          description: sub.description || '',
          categoryId: sub.categoryId || sub.parentId || '',
          parentId: sub.parentId,
          order: sub.order,
          isActive: sub.isActive === undefined ? true : sub.isActive,
          imageUrl: sub.imageUrl,
          createdAt: typeof sub.createdAt === 'string' ? new Date(sub.createdAt) : sub.createdAt,
          updatedAt: typeof sub.updatedAt === 'string' ? new Date(sub.updatedAt) : sub.updatedAt
        }));
        setSubcategories(transformedSubs);
      }
      
      setIsSaving(false);
    } catch (err: any) {
      setIsSaving(false);
      console.error('Error moving category up:', err);
      setSnackbar({
        open: true,
        message: err.message || 'حدث خطأ أثناء تحريك الفئة',
        severity: 'error',
      });
    }
  };
  
  // نقل الفئة للأسفل
  const handleMoveDown = async () => {
    if (!selectedActionCategory) {
      handleCloseActionMenu();
      return;
    }
    
    try {
      setIsSaving(true);
      await moveCategoryPosition(selectedActionCategory.id, 'down');
      
      setSnackbar({
        open: true,
        message: 'تم تحريك الفئة لأسفل بنجاح',
        severity: 'success',
      });
      
      // إغلاق القائمة المنبثقة
      handleCloseActionMenu();
      
      // إعادة تحميل البيانات بعد التحريك
      await loadCategories();
      
      // إذا كانت الفئة فرعية، نعيد تحميل الفئات الفرعية
      if (selectedActionCategory.parentId && selectedCategory) {
        const fetchedSubs = await getSubcategories(selectedCategory.id);
        const transformedSubs: SubCategory[] = fetchedSubs.map(sub => ({
          id: sub.id,
          name: sub.name || '',
          description: sub.description || '',
          categoryId: sub.categoryId || sub.parentId || '',
          parentId: sub.parentId,
          order: sub.order,
          isActive: sub.isActive === undefined ? true : sub.isActive,
          imageUrl: sub.imageUrl,
          createdAt: typeof sub.createdAt === 'string' ? new Date(sub.createdAt) : sub.createdAt,
          updatedAt: typeof sub.updatedAt === 'string' ? new Date(sub.updatedAt) : sub.updatedAt
        }));
        setSubcategories(transformedSubs);
      }
      
      setIsSaving(false);
    } catch (err: any) {
      setIsSaving(false);
      console.error('Error moving category down:', err);
      setSnackbar({
        open: true,
        message: err.message || 'حدث خطأ أثناء تحريك الفئة',
        severity: 'error',
      });
    }
  };

  return (
    <Container maxWidth="lg">
      <Box my={4}>
        <Grid container spacing={2} alignItems="center" mb={4}>
          <Grid item xs>
            <Typography variant="h4" component="h1" gutterBottom>
              <CategoryIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
              إدارة الفئات
            </Typography>
          </Grid>
          <Grid item>
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={() => handleAddCategory(false)}
              disabled={isSaving}
            >
              إضافة فئة جديدة
            </Button>
          </Grid>
          <Grid item>
            <Button
              variant="outlined"
              color="primary"
              startIcon={isSaving ? <CircularProgress size={20} /> : <RefreshIcon />}
              onClick={loadCategories}
              disabled={isSaving || loading}
            >
              {isSaving ? 'جاري التحديث...' : 'تحديث'}
            </Button>
          </Grid>
          <Grid item>
            <Button
              variant="outlined"
              color="error"
              startIcon={isDeletingAll ? <CircularProgress size={20} /> : <DeleteIcon />}
              onClick={() => setOpenDeleteAllDialog(true)}
              disabled={isSaving || loading || isDeletingAll}
            >
              حذف كافة الفئات
            </Button>
          </Grid>
        </Grid>

        {loading ? (
          <Box display="flex" justifyContent="center" p={5}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Alert severity="error">{error}</Alert>
        ) : (
          <Grid container spacing={3}>
            {/* قائمة الفئات الرئيسية */}
            <Grid item xs={12} md={4}>
              <Paper elevation={3} sx={{ height: '100%', minHeight: 400 }}>
                <Box p={2} sx={{ borderBottom: 1, borderColor: 'divider' }}>
                  <Typography variant="h6">الفئات الرئيسية</Typography>
                </Box>
                
                {mainCategories.length === 0 ? (
                  <Box p={3} textAlign="center">
                    <Typography color="text.secondary">لا توجد فئات رئيسية</Typography>
                    <Button
                      variant="contained"
                      size="small"
                      startIcon={<AddIcon />}
                      onClick={() => handleAddCategory(false)}
                      sx={{ mt: 2 }}
                      disabled={isSaving}
                    >
                      إضافة فئة جديدة
                    </Button>
                  </Box>
                ) : (
                  <Box sx={{ maxHeight: 600, overflow: 'auto' }}>
                    <List>
                      {mainCategories.map((category) => (
                        <ListItemButton
                          key={category.id}
                          selected={isSelected(category)}
                          onClick={() => handleSelectCategory(category)}
                          disabled={isSaving}
                        >
                          <Grid container alignItems="center" spacing={1}>
                            <Grid item xs={2}>
                              <Avatar
                                src={category.imageUrl || undefined}
                                sx={{ 
                                  width: 40, 
                                  height: 40, 
                                  bgcolor: isSelected(category) ? 'primary.main' : 'grey.300' 
                                }}
                              >
                                {!category.imageUrl && <CategoryIcon />}
                              </Avatar>
                            </Grid>
                            <Grid item xs={7}>
                              <Typography variant="subtitle1">{category.name}</Typography>
                              {!category.isActive && (
                                <Chip 
                                  size="small" 
                                  label="غير نشطة" 
                                  color="default" 
                                  variant="outlined" 
                                  sx={{ fontSize: '0.7rem' }}
                                />
                              )}
                            </Grid>
                            <Grid item xs={3} textAlign="right">
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleEditCategory(category.id);
                                }}
                                disabled={isSaving}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                              <IconButton
                                size="small"
                                color="error"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDeleteConfirm(category);
                                }}
                                disabled={isSaving}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                              <IconButton
                                size="small"
                                color="default"
                                onClick={(e) => handleOpenActionMenu(e, category)}
                                disabled={isSaving}
                              >
                                <MoreVertIcon fontSize="small" />
                              </IconButton>
                            </Grid>
                          </Grid>
                        </ListItemButton>
                      ))}
                    </List>
                  </Box>
                )}
              </Paper>
            </Grid>

            {/* تفاصيل الفئة والفئات الفرعية */}
            <Grid item xs={12} md={8}>
              <Paper elevation={3} sx={{ height: '100%', minHeight: 400 }}>
                {selectedCategory ? (
                  <>
                    <Box p={2} sx={{ borderBottom: 1, borderColor: 'divider' }}>
                      <Grid container alignItems="center" spacing={2}>
                        <Grid item>
                          <Avatar
                            src={selectedCategory.imageUrl || undefined}
                            sx={{ width: 50, height: 50, bgcolor: 'primary.main' }}
                          >
                            {!selectedCategory.imageUrl && <CategoryIcon />}
                          </Avatar>
                        </Grid>
                        <Grid item xs>
                          <Typography variant="h6">{selectedCategory.name}</Typography>
                          <Typography variant="body2" color="text.secondary">
                            {selectedCategory.description || 'لا يوجد وصف'}
                          </Typography>
                        </Grid>
                        <Grid item>
                          <Button
                            variant="contained"
                            size="small"
                            startIcon={<AddIcon />}
                            onClick={() => handleAddCategory(true)}
                          >
                            إضافة فئة فرعية
                          </Button>
                        </Grid>
                      </Grid>
                    </Box>

                    <Box p={2}>
                      <Typography variant="subtitle1" gutterBottom>
                        الفئات الفرعية
                      </Typography>
                      
                      {subcategories.length === 0 ? (
                        <Box py={3} textAlign="center">
                          <Typography color="text.secondary">
                            لا توجد فئات فرعية لهذه الفئة
                          </Typography>
                          <Button
                            variant="outlined"
                            size="small"
                            startIcon={<AddIcon />}
                            onClick={() => handleAddCategory(true)}
                            sx={{ mt: 2 }}
                          >
                            إضافة فئة فرعية
                          </Button>
                        </Box>
                      ) : (
                        <TableContainer>
                          <Table>
                            <TableHead>
                              <TableRow>
                                <TableCell>الفئة الفرعية</TableCell>
                                <TableCell>الوصف</TableCell>
                                <TableCell align="center">الحالة</TableCell>
                                <TableCell align="center">الإجراءات</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {subcategories.map((subcategory) => (
                                <TableRow key={subcategory.id}>
                                  <TableCell>
                                    <Box display="flex" alignItems="center">
                                      <Avatar
                                        src={subcategory.imageUrl || undefined}
                                        sx={{ width: 30, height: 30, mr: 1 }}
                                        variant="rounded"
                                      >
                                        {!subcategory.imageUrl && <CategoryIcon fontSize="small" />}
                                      </Avatar>
                                      {subcategory.name}
                                    </Box>
                                  </TableCell>
                                  <TableCell>{subcategory.description || "—"}</TableCell>
                                  <TableCell align="center">
                                    {subcategory.isActive ? (
                                      <Chip size="small" label="نشطة" color="success" />
                                    ) : (
                                      <Chip size="small" label="غير نشطة" color="default" />
                                    )}
                                  </TableCell>
                                  <TableCell align="center">
                                    <Tooltip title="تعديل">
                                      <IconButton
                                        size="small"
                                        color="primary"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleEditCategory(subcategory.id);
                                        }}
                                        disabled={isSaving}
                                      >
                                        <EditIcon fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                    <Tooltip title="حذف">
                                      <IconButton
                                        size="small"
                                        color="error"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleDeleteConfirm(subcategory);
                                        }}
                                        disabled={isSaving}
                                      >
                                        <DeleteIcon fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                    <Tooltip title="المزيد من الإجراءات">
                                      <IconButton
                                        size="small"
                                        color="default"
                                        onClick={(e) => handleOpenActionMenu(e, subcategory)}
                                        disabled={isSaving}
                                      >
                                        <MoreVertIcon fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      )}
                    </Box>
                  </>
                ) : (
                  <Box
                    display="flex"
                    flexDirection="column"
                    alignItems="center"
                    justifyContent="center"
                    p={5}
                    height="100%"
                  >
                    <CategoryIcon color="disabled" sx={{ fontSize: 60, mb: 2 }} />
                    <Typography color="text.secondary" variant="h6" align="center">
                      اختر فئة من القائمة لعرض التفاصيل والفئات الفرعية
                    </Typography>
                    <Typography color="text.secondary" variant="body2" align="center" sx={{ mt: 1 }}>
                      أو قم بإضافة فئة جديدة باستخدام زر "إضافة فئة جديدة"
                    </Typography>
                  </Box>
                )}
              </Paper>
            </Grid>
          </Grid>
        )}

        {/* حوار إضافة/تعديل فئة */}
        <Dialog
          open={openDialog}
          onClose={() => setOpenDialog(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>
            {isEditMode 
              ? `تعديل ${isSubcategory ? 'الفئة الفرعية' : 'الفئة'}`
              : `إضافة ${isSubcategory ? 'فئة فرعية' : 'فئة جديدة'}`
            }
          </DialogTitle>
          <DialogContent>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="اسم الفئة"
                  name="name"
                  value={currentCategory.name || ''}
                  onChange={handleInputChange}
                  required
                />
              </Grid>
              
              {isSubcategory && (
                <Grid item xs={12}>
                  <FormControl fullWidth>
                    <InputLabel>الفئة الرئيسية</InputLabel>
                    <Select
                      value={currentCategory.parentId || ''}
                      onChange={handleParentChange}
                      label="الفئة الرئيسية"
                      required
                    >
                      {mainCategories.map(category => (
                        <MenuItem key={category.id} value={category.id}>
                          {category.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              )}
              
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="الوصف"
                  name="description"
                  value={currentCategory.description || ''}
                  onChange={handleInputChange}
                  multiline
                  rows={3}
                />
              </Grid>
              
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={currentCategory.isActive !== undefined ? currentCategory.isActive : true}
                      onChange={handleInputChange}
                      name="isActive"
                    />
                  }
                  label="نشطة"
                />
              </Grid>
              
              <Grid item xs={12}>
                <Typography variant="subtitle2" gutterBottom>
                  صورة الفئة
                </Typography>
                
                <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                  <Button
                    variant="outlined"
                    component="label"
                    startIcon={<CloudUploadIcon />}
                  >
                    تحميل صورة
                    <input
                      type="file"
                      accept="image/*"
                      hidden
                      onChange={handleImageUpload}
                    />
                  </Button>
                  
                  <TextField
                    sx={{ ml: 2, flexGrow: 1 }}
                    size="small"
                    label="أو أدخل رابط الصورة"
                    name="imageUrl"
                    value={selectedImage ? '' : (currentCategory.imageUrl || '')}
                    onChange={handleInputChange}
                    disabled={!!selectedImage}
                    InputProps={{
                      startAdornment: <InputAdornment position="start"><LinkIcon /></InputAdornment>,
                    }}
                  />

                  {/* زر اختبار Firebase Storage */}
                  <Button
                    sx={{ ml: 1 }}
                    variant="outlined"
                    color="secondary"
                    onClick={handleTestStorage}
                    disabled={isSaving}
                  >
                    اختبار التخزين
                  </Button>
                </Box>
                
                {previewImage && (
                  <Box mt={2} textAlign="center">
                    <img
                      src={previewImage}
                      alt="معاينة الصورة"
                      style={{
                        maxWidth: '100%',
                        maxHeight: 200,
                        objectFit: 'contain',
                        border: '1px solid #ddd',
                        borderRadius: 4
                      }}
                    />
                  </Box>
                )}
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenDialog(false)} disabled={isSaving}>
              إلغاء
            </Button>
            <Button 
              onClick={handleSaveCategory} 
              variant="contained" 
              color="primary"
              disabled={isSaving}
              startIcon={isSaving ? <CircularProgress size={20} /> : null}
            >
              {isSaving 
                ? 'جاري الحفظ...' 
                : (isEditMode ? 'حفظ التغييرات' : 'إضافة')
              }
            </Button>
          </DialogActions>
        </Dialog>

        {/* حوار تأكيد الحذف */}
        <Dialog
          open={openDeleteDialog}
          onClose={() => setOpenDeleteDialog(false)}
        >
          <DialogTitle>
            تأكيد الحذف
          </DialogTitle>
          <DialogContent>
            <Typography>
              هل أنت متأكد من حذف {currentCategory.parentId ? 'الفئة الفرعية' : 'الفئة'} "{currentCategory.name}"؟
              {!currentCategory.parentId && ' سيتم حذف جميع الفئات الفرعية المرتبطة بها.'}
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenDeleteDialog(false)}>
              إلغاء
            </Button>
            <Button onClick={handleDeleteCategory} color="error" variant="contained">
              حذف
            </Button>
          </DialogActions>
        </Dialog>

        {/* حوار تأكيد حذف كافة الفئات */}
        <Dialog
          open={openDeleteAllDialog}
          onClose={() => setOpenDeleteAllDialog(false)}
          aria-labelledby="delete-all-categories-dialog-title"
        >
          <DialogTitle id="delete-all-categories-dialog-title">
            حذف كافة الفئات
          </DialogTitle>
          <DialogContent>
            <Typography color="error">
              تحذير: هذا الإجراء سيؤدي إلى حذف <strong>جميع الفئات ومحتوياتها</strong> من قاعدة البيانات.
            </Typography>
            <Typography sx={{ mt: 2 }}>
              هذا الإجراء لا يمكن التراجع عنه. هل أنت متأكد من رغبتك في حذف جميع الفئات؟
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button 
              onClick={() => setOpenDeleteAllDialog(false)} 
              color="inherit"
              disabled={isDeletingAll}
            >
              إلغاء
            </Button>
            <Button 
              onClick={handleDeleteAllCategories} 
              color="error" 
              variant="contained"
              disabled={isDeletingAll}
            >
              {isDeletingAll ? (
                <>
                  <CircularProgress size={20} color="inherit" sx={{ mr: 1 }} />
                  جاري الحذف...
                </>
              ) : (
                'حذف كافة الفئات'
              )}
            </Button>
          </DialogActions>
        </Dialog>

        {/* الإشعارات */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={handleCloseSnackbar}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        >
          <Alert
            onClose={handleCloseSnackbar}
            severity={snackbar.severity}
            variant="filled"
            sx={{ width: '100%' }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>

        {/* قائمة الإجراءات الإضافية */}
        <Menu
          anchorEl={actionMenuAnchorEl}
          open={Boolean(actionMenuAnchorEl)}
          onClose={handleCloseActionMenu}
        >
          <MenuItem 
            onClick={handleMoveUp}
            disabled={isSaving}
          >
            <ListItemIcon>
              <ArrowUpwardIcon fontSize="small" />
            </ListItemIcon>
            <Typography variant="inherit">تحريك لأعلى</Typography>
          </MenuItem>
          <MenuItem 
            onClick={handleMoveDown}
            disabled={isSaving}
          >
            <ListItemIcon>
              <ArrowDownwardIcon fontSize="small" />
            </ListItemIcon>
            <Typography variant="inherit">تحريك لأسفل</Typography>
          </MenuItem>
          <Divider />
          <MenuItem 
            onClick={() => {
              handleCloseActionMenu();
              handleOpenConvertDialog();
            }}
            disabled={isSaving}
          >
            <ListItemIcon>
              <SwapVertIcon fontSize="small" />
            </ListItemIcon>
            <Typography variant="inherit">
              {selectedActionCategory?.parentId 
                ? 'تحويل إلى فئة رئيسية' 
                : 'تحويل إلى فئة فرعية'
              }
            </Typography>
          </MenuItem>
          <Divider />
          <MenuItem 
            onClick={() => {
              handleCloseActionMenu();
              if (selectedActionCategory) {
                handleEditCategory(selectedActionCategory.id);
              }
            }}
            disabled={isSaving}
          >
            <ListItemIcon>
              <EditIcon fontSize="small" />
            </ListItemIcon>
            <Typography variant="inherit">تعديل الفئة</Typography>
          </MenuItem>
          <MenuItem 
            onClick={() => {
              handleCloseActionMenu();
              if (selectedActionCategory) {
                handleDeleteConfirm(selectedActionCategory);
              }
            }}
            disabled={isSaving}
            sx={{ color: 'error.main' }}
          >
            <ListItemIcon>
              <DeleteIcon fontSize="small" color="error" />
            </ListItemIcon>
            <Typography variant="inherit">حذف الفئة</Typography>
          </MenuItem>
        </Menu>
        
        {/* حوار تحويل الفئة إلى فئة فرعية */}
        <Dialog
          open={convertCategoryDialog}
          onClose={() => !isSaving && setConvertCategoryDialog(false)}
        >
          <DialogTitle>
            تحويل الفئة إلى فئة فرعية
          </DialogTitle>
          <DialogContent>
            <Typography gutterBottom>
              اختر الفئة الرئيسية التي تريد وضع "{selectedActionCategory?.name}" تحتها:
            </Typography>
            <FormControl fullWidth sx={{ mt: 2 }}>
              <InputLabel>الفئة الرئيسية</InputLabel>
              <Select
                value={convertTargetParent}
                onChange={(e) => setConvertTargetParent(e.target.value)}
                disabled={isSaving}
              >
                {mainCategories
                  .filter(cat => cat.id !== selectedActionCategory?.id) // استبعاد الفئة الحالية
                  .map(category => (
                    <MenuItem key={category.id} value={category.id}>
                      {category.name}
                    </MenuItem>
                  ))
                }
              </Select>
            </FormControl>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setConvertCategoryDialog(false)} disabled={isSaving}>
              إلغاء
            </Button>
            <Button
              onClick={handleConvertToSubcategory}
              variant="contained"
              color="primary"
              disabled={!convertTargetParent || isSaving}
              startIcon={isSaving ? <CircularProgress size={20} /> : null}
            >
              {isSaving ? 'جاري التحويل...' : 'تحويل'}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Container>
  );
};

export default CategoryPage; 

